import { useEffect, useMemo, useState } from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors, useDraggable, useDroppable } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CheckCircle2, GripVertical, Smartphone } from "lucide-react";
import useMobileOrientation from "@/hooks/useMobileOrientation";
import LandscapePrompt from "@/components/activities/LandscapePrompt";

// Shared shuffle
const shuffle = (arr) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

/* ============ PAIRS MODE ============ */
const DraggableTerm = ({ id, label, placed }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id, disabled: placed });
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: placed ? 0.4 : isDragging ? 0.6 : 1,
    cursor: placed ? "default" : "grab",
  };
  return (
    <button
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      type="button"
      className={`px-4 py-3 rounded-xl border-2 font-heading font-bold text-left transition-colors ${placed ? "bg-mm-steel/10 border-mm-steel/20 text-mm-navy/50" : "bg-white border-mm-navy/20 text-mm-navy hover:border-mm-steel"}`}
      data-testid={`drag-term-${id}`}
    >
      <GripVertical size={14} className="inline -mt-1 mr-1 text-mm-steel" /> {label}
    </button>
  );
};

const DroppableDef = ({ id, label, placedTerm, correct }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  const tone = placedTerm
    ? (correct === true ? "border-mm-steel bg-mm-steel/10" : correct === false ? "border-red-400 bg-red-50" : "border-mm-gold bg-mm-gold/10")
    : isOver ? "border-mm-gold bg-mm-gold/5 border-dashed" : "border-dashed border-mm-navy/20";
  return (
    <div ref={setNodeRef} className={`rounded-xl border-2 p-3 min-h-[64px] ${tone}`} data-testid={`drag-def-${id}`}>
      <div className="text-xs font-bold text-mm-steel uppercase tracking-widest">Definition</div>
      <div className="text-mm-navy mt-0.5">{label}</div>
      {placedTerm && <div className="mt-2 text-xs font-bold text-mm-navy">→ {placedTerm}</div>}
    </div>
  );
};

const PairsBoard = ({ pairs, completed, onComplete, busy }) => {
  // Build stable IDs
  const items = useMemo(() => pairs.map((p, i) => ({ id: `t${i}`, left: p.left, defId: `d${i}`, right: p.right })), [pairs]);
  const shuffledTerms = useMemo(() => shuffle(items.map((it) => ({ id: it.id, label: it.left }))), [items]);
  const shuffledDefs = useMemo(() => shuffle(items.map((it) => ({ id: it.defId, label: it.right, correctTermId: it.id }))), [items]);

  const [placements, setPlacements] = useState({}); // defId -> termId
  const [checked, setChecked] = useState(null); // null | true (allCorrect) | false

  const sensors = useSensors(useSensor(PointerSensor), useSensor(TouchSensor), useSensor(KeyboardSensor));

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over) return;
    // Drop term onto definition: place; replace any existing placement of the term elsewhere
    const newPlacements = { ...placements };
    for (const k of Object.keys(newPlacements)) if (newPlacements[k] === active.id) delete newPlacements[k];
    newPlacements[over.id] = active.id;
    setPlacements(newPlacements);
    setChecked(null);
  };

  const allPlaced = Object.keys(placements).length === items.length;
  const allCorrect = items.every((it) => placements[it.defId] === it.id);

  const verifyAndComplete = () => {
    if (!allPlaced) return;
    if (allCorrect) {
      onComplete();
    } else {
      setChecked(false);
    }
  };

  return (
    <div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-mm-steel mb-2">Terms (drag)</div>
            <div className="flex flex-col gap-2">
              {shuffledTerms.map((t) => {
                const placedAt = Object.entries(placements).find(([, termId]) => termId === t.id)?.[0];
                return <DraggableTerm key={t.id} id={t.id} label={t.label} placed={!!placedAt} />;
              })}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-mm-steel mb-2">Definitions (drop)</div>
            <div className="flex flex-col gap-2">
              {shuffledDefs.map((d) => {
                const placedTermId = placements[d.id];
                const placedTerm = items.find((it) => it.id === placedTermId)?.left;
                const correct = checked === false ? placedTermId === d.correctTermId : null;
                return <DroppableDef key={d.id} id={d.id} label={d.label} placedTerm={placedTerm} correct={correct} />;
              })}
            </div>
          </div>
        </div>
      </DndContext>

      {!completed && (
        <button
          onClick={verifyAndComplete}
          disabled={!allPlaced || busy}
          className="btn-trail btn-gold w-full mt-5 disabled:opacity-50"
          data-testid="dragmatch-complete"
        >
          {busy ? "Planting marker..." : allPlaced ? "Check & complete (+20 AP)" : `Match all ${items.length} pairs first`}
        </button>
      )}
      {checked === false && !completed && (
        <div className="mt-3 text-sm text-red-600 font-bold" data-testid="dragmatch-error">Not quite — red boxes show mismatches. Try again.</div>
      )}
      {completed && (
        <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-mm-steel/15 text-mm-navy rounded-full font-bold">
          <CheckCircle2 size={16} strokeWidth={2.5} /> Trail marker planted · +20 AP
        </div>
      )}
    </div>
  );
};

/* ============ ORDER MODE ============ */
const SortableStep = ({ id, label, idx }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="bg-white border-2 border-mm-navy/15 rounded-xl px-4 py-3 flex items-center gap-3 cursor-grab active:cursor-grabbing"
      data-testid={`drag-step-${id}`}
    >
      <span className="w-8 h-8 rounded-full bg-mm-navy text-white font-heading font-black flex items-center justify-center">{idx + 1}</span>
      <GripVertical size={14} className="text-mm-steel" />
      <span className="font-bold text-mm-navy">{label}</span>
    </div>
  );
};

const OrderBoard = ({ steps, completed, onComplete, busy }) => {
  const itemsCorrect = useMemo(() => steps.map((s, i) => ({ id: `s${i}`, label: s })), [steps]);
  const [items, setItems] = useState(() => shuffle(itemsCorrect));
  const [checked, setChecked] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor), useSensor(TouchSensor), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  }));

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((x) => x.id === active.id);
    const newIdx = items.findIndex((x) => x.id === over.id);
    setItems(arrayMove(items, oldIdx, newIdx));
    setChecked(null);
  };

  const verify = () => {
    const isOrdered = items.every((it, i) => it.id === itemsCorrect[i].id);
    if (isOrdered) onComplete();
    else setChecked(false);
  };

  return (
    <div>
      <div className="text-xs uppercase tracking-widest font-bold text-mm-steel mb-2">Drag into the correct order</div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          <div className="flex flex-col gap-2">
            {items.map((it, idx) => <SortableStep key={it.id} id={it.id} label={it.label} idx={idx} />)}
          </div>
        </SortableContext>
      </DndContext>
      {!completed && (
        <button onClick={verify} disabled={busy} className="btn-trail btn-gold w-full mt-5 disabled:opacity-50" data-testid="dragorder-complete">
          {busy ? "Planting marker..." : "Check & complete (+20 AP)"}
        </button>
      )}
      {checked === false && !completed && (
        <div className="mt-3 text-sm text-red-600 font-bold">Not in order yet — keep climbing!</div>
      )}
      {completed && (
        <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-mm-steel/15 text-mm-navy rounded-full font-bold">
          <CheckCircle2 size={16} strokeWidth={2.5} /> Trail marker planted · +20 AP
        </div>
      )}
    </div>
  );
};

const DragDropMatchActivity = ({ activity, completed, onComplete, busy }) => {
  const data = activity?.data || {};
  const { isMobile, isPortrait } = useMobileOrientation();
  // Track whether the user has ever "entered" the activity — either by
  // rotating to landscape or by tapping Continue Anyway. Once entered, we
  // NEVER re-show the prompt (which would unmount the board and reset
  // progress); we only surface the non-intrusive tip banner instead.
  const [hasEntered, setHasEntered] = useState(() => !(isMobile && isPortrait));

  useEffect(() => {
    // Landscape (or non-mobile) implicitly means the user has "entered" — flip
    // the flag so future rotations back to portrait don't reset progress.
    if (!hasEntered && (!isMobile || !isPortrait)) {
      setHasEntered(true);
    }
  }, [isMobile, isPortrait, hasEntered]);

  const shouldShowPrompt = isMobile && isPortrait && !hasEntered && !completed;
  const showPortraitTip = isMobile && isPortrait && hasEntered && !completed;

  if (shouldShowPrompt) {
    return <LandscapePrompt onContinue={() => setHasEntered(true)} />;
  }

  const board = (data.mode === "order" && Array.isArray(data.steps)) ? (
    <OrderBoard steps={data.steps} completed={completed} onComplete={onComplete} busy={busy} />
  ) : (
    <PairsBoard pairs={Array.isArray(data.pairs) ? data.pairs : []} completed={completed} onComplete={onComplete} busy={busy} />
  );

  return (
    <div>
      {showPortraitTip && (
        <div
          className="mb-3 flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-bold"
          style={{ backgroundColor: "#D4A84322", color: "#1B3A5C" }}
          data-testid="landscape-tip-banner"
        >
          <Smartphone size={14} strokeWidth={2.5} />
          Tip: rotate your phone sideways for a better experience
        </div>
      )}
      {board}
    </div>
  );
};

export default DragDropMatchActivity;
